// webapp/src/LoginPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { beginGuestUpgrade } from './guestSession';
import EmailConfirmationPanel from './EmailConfirmationPanel';
import { authErrorParams, authErrorType } from './authErrors';
import { useGuestCopy } from './guestCopy';
import { supabase } from './supabase';
import { signInWithOAuth } from './oauth';
import { appUrl } from './appNavigation';
import config_names from './config_names';
import logo from './logo.svg';
import { 
  FiMail, 
  FiAlertCircle, 
  FiArrowLeft,
  FiCheckCircle
} from 'react-icons/fi';
import { FaApple, FaGoogle } from 'react-icons/fa';
import LanguageSelector from './LanguageSelector';
import { useI18n } from './i18n';
import { classifyError } from './analytics';
import { trackAuthEvent, trackAuthPageView } from './authAttribution';
import { getAuthRedirectPath, getAuthRedirectUrl } from './authRedirect';
import './auth-pages.css';

const getErrorMessage = (error, t) => {
  if (!error) return t('auth.unexpected');

  switch (error.message) {
    case 'Invalid login credentials':
      return t('auth.invalidCredentials');
    case 'Email not confirmed':
      return t('auth.emailNotConfirmed');
    default:
      return error.message || t('auth.unexpected');
  }
};

function LoginPage() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(Boolean(location.state?.showEmailLogin));
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const copy = useGuestCopy();
  const { t } = useI18n();
  const authRedirectPath = getAuthRedirectPath(location.state);
  const accountDeleted = location.state?.accountDeleted === true;

  useEffect(() => {
    trackAuthPageView('login');
  }, []);

  const handleGoogleLogin = async () => {
    try {
      trackAuthEvent('login_attempt', { method: 'google' });
      const { error } = await signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthRedirectUrl(location.state, {
            auth_method: 'google',
            auth_target: 'login',
          })
        }
      });
  
      if (error) throw error;
      // The redirect will be handled by Google OAuth
    } catch (error) {
      trackAuthEvent('login_error', { method: 'google', ...authErrorParams(error, 'oauth_start') });
      setError(getErrorMessage(error, t));
    }
  };

  const handleAppleLogin = async () => {
    try {
      trackAuthEvent('login_attempt', { method: 'apple' });
      const { error } = await signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: getAuthRedirectUrl(location.state, {
            auth_method: 'apple',
            auth_target: 'login',
          })
        }
      });

      if (error) throw error;
      // The redirect will be handled by Apple OAuth
    } catch (error) {
      trackAuthEvent('login_error', { method: 'apple', ...authErrorParams(error, 'oauth_start') });
      setError(getErrorMessage(error, t));
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError(null);
    trackAuthEvent('login_attempt', { method: 'email' });
    
    try {
      await beginGuestUpgrade();
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      });
  
      if (error) throw error;
      trackAuthEvent('login_success', { method: 'email' });
      
      navigate(authRedirectPath, { replace: true });
    } catch (error) {
      trackAuthEvent('login_error', { method: 'email', ...authErrorParams(error, 'password_login') });
      if (error?.code === 'email_not_confirmed') setUnconfirmedEmail(email.trim());
      setError(getErrorMessage(error, t));
    }
  };

  const handlePasswordReset = async (event) => {
    event.preventDefault();
    setResetError(null);
    setResetSuccess('');
    setResetLoading(true);
    trackAuthEvent('password_reset_request', { step: 'submit' });

    try {
      await beginGuestUpgrade();
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: appUrl(`/reset-password?redirect_path=${encodeURIComponent(authRedirectPath)}`)
      });

      if (error) throw error;
      trackAuthEvent('password_reset_request', { step: 'success' });
      setResetSuccess(t('auth.resetSent'));
    } catch (error) {
      trackAuthEvent('password_reset_request', { step: 'error', error_type: classifyError(error) });
      setResetError(error.message || t('auth.resetFailed'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleEmailLink = async event => {
    const emailInput = event.currentTarget.form.querySelector('input[type="email"]');
    if (!emailInput.reportValidity() || linkLoading) return;
    setLinkLoading(true); setError(null); setLinkSuccess('');
    trackAuthEvent('login_attempt', { method: 'email_link' });
    try {
      await beginGuestUpgrade();
      const emailRedirectTo = location.state?.finishGuestEmail
        ? appUrl(`/signup?finish_guest_email=1&redirect_path=${encodeURIComponent(authRedirectPath)}`)
        : getAuthRedirectUrl(location.state, { auth_method: 'email', auth_target: 'login' });
      const { error: failure } = await supabase.auth.signInWithOtp({
        email: email.trim(), options: { shouldCreateUser: false, emailRedirectTo },
      });
      if (failure) throw failure;
      trackAuthEvent('login_email_link_sent', { method: 'email_link' });
      setLinkSuccess(copy.signinLinkSent);
    } catch (failure) {
      trackAuthEvent('login_error', { method: 'email_link', ...authErrorParams(failure, 'email_link') });
      setError(authErrorType(failure) === 'rate_limit' ? copy.recoveryRate : copy.signinLinkFailed);
    } finally { setLinkLoading(false); }
  };

  const showPasswordResetForm = () => {
    trackAuthEvent('password_reset_open');
    setShowPasswordReset(true);
    setResetEmail(email);
    setResetError(null);
    setResetSuccess('');
    setError(null);
  };

  const handleBackToEmailLogin = () => {
    setShowPasswordReset(false);
    setResetError(null);
    setResetSuccess('');
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-topline">
          <div className="auth-brand">
            <img src={logo} alt="" />
            <span>{config_names.projectName}</span>
          </div>
          <LanguageSelector />
        </div>
        <div className="auth-header">
          <h1>{showPasswordReset ? t('auth.resetTitle') : t('auth.loginTitle')}</h1>
          <p>
            {showPasswordReset
              ? t('auth.resetSubtitle')
              : t('auth.loginSubtitle', { project: config_names.projectName })}
          </p>
        </div>

        {error && !showPasswordReset && (
          <div className="auth-error" role="alert">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {unconfirmedEmail && unconfirmedEmail === email.trim() && !showPasswordReset && <EmailConfirmationPanel
          email={unconfirmedEmail} initialCooldown={0} redirectPath={authRedirectPath}
          redirectUrl={getAuthRedirectUrl(location.state, { auth_method: 'email', auth_target: 'signup' })} />}
        {linkSuccess && !showPasswordReset && <p className="auth-success" role="status">{linkSuccess}</p>}

        {accountDeleted && !showPasswordReset && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{t('auth.accountDeleted')}</span>
          </div>
        )}

        {resetError && showPasswordReset && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{resetError}</span>
          </div>
        )}

        {resetSuccess && showPasswordReset && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{resetSuccess}</span>
          </div>
        )}

        {showPasswordReset ? (
          <form className="auth-form" onSubmit={handlePasswordReset}>
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder={t('common.email')}
                  value={resetEmail} 
                  onChange={(e) => setResetEmail(e.target.value)} 
                  required 
                />
              </div>
            </div>

            <button type="submit" className="auth-btn primary-btn" disabled={resetLoading}>
              <FiMail /> {resetLoading ? t('common.sending') : t('auth.sendReset')}
            </button>

            <button 
              type="button" 
              className="auth-btn secondary-btn" 
              onClick={handleBackToEmailLogin}
              disabled={resetLoading}
            >
              <FiArrowLeft /> {t('common.back')}
            </button>
          </form>
        ) : !showEmailLogin ? (
          <>
            <button 
              className="auth-btn apple-btn" 
              onClick={handleAppleLogin}
            >
              <FaApple /> {t('auth.signInApple')}
            </button>

            <button 
              className="auth-btn google-btn" 
              onClick={handleGoogleLogin}
            >
              <FaGoogle /> {t('auth.signInGoogle')}
            </button>
            
            <div className="auth-divider">{t('auth.or')}</div>
            
            <button 
              className="auth-btn primary-btn" 
              onClick={() => {
                setShowEmailLogin(true);
                setShowPasswordReset(false);
                setError(null);
                trackAuthEvent('login_email_form_open');
              }}
            >
              <FiMail /> {t('auth.signInEmail')}
            </button>
          </>
        ) : (
          <form className="auth-form" onSubmit={handleLogin}>
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder={t('common.email')}
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="password" 
                  className="form-control" 
                  placeholder={t('common.password')}
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
              </div>
            </div>

            <div className="auth-link-row">
              <button
                type="button"
                className="auth-text-link"
                onClick={showPasswordResetForm}
              >
                {t('auth.forgotPassword')}
              </button>
            </div>
            
            <button type="submit" className="auth-btn primary-btn" disabled={linkLoading}>
              <FiMail /> {t('common.signIn')}
            </button>
            <button type="button" className="auth-btn secondary-btn" onClick={handleEmailLink} disabled={linkLoading}>
              <FiMail /> {linkLoading ? copy.waiting : copy.sendSigninLink}
            </button>
            
            <button 
              type="button" 
              className="auth-btn secondary-btn" 
              onClick={() => {
                setShowEmailLogin(false);
                setShowPasswordReset(false);
                setError(null);
                trackAuthEvent('login_email_form_close');
              }}
            >
              <FiArrowLeft /> {t('common.back')}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <span>{t('auth.newToProject', { project: config_names.projectName })} </span>
          <Link to="/signup" state={{ from: authRedirectPath }}>{t('auth.signUpNow')}</Link>
        </div>

        <div className="auth-legal">
          {t('auth.legal', { project: config_names.projectName_genitive })}{' '}
          <a target="_top" href={`${config_names.domain}/terms`}>{t('auth.terms')}</a> {t('auth.and')}{' '}
          <a target="_top" href={`${config_names.domain}/privacy`}>{t('auth.privacy')}</a>.
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
